import { useEffect, useState } from 'react';

/**
 * Cross-window selection bridge (#197 / spec §0).
 *
 * The operations panel is a second screen the operator works while the COP (main
 * map) reacts. When a queue row is clicked on `/panel`, it broadcasts a compact
 * selection on a same-origin BroadcastChannel; the COP picks it up and opens that
 * entity exactly as a click / `#deep-link` would (fetch the detail, select it,
 * fly the map). One-way by design — the panel drives, the COP responds.
 */

export const SELECTION_CHANNEL_NAME = 'tsr-selection';

/** The queue/entity kinds the panel can hand to the COP. Mirrors the COP's
 *  deep-link-style open handlers (camera + the five Urgent-Alert sources). The
 *  #206 cross-link kinds (`evacuationOrder` opens the order panel; `evacuation`
 *  opens the §4.4 Logistics panel; `incident`/`community` fly the map) ride the
 *  same in-window `selectOnCop` path. */
export type BridgeSelectionKind =
  | 'camera'
  | 'hotspot'
  | 'cap'
  | 'smoke'
  | 'ice'
  | 'flood'
  // #184 media-signal spike — the COP-side fall-through flies to the centroid.
  | 'media'
  // #444 OC-fire urgent alert — like the other anomaly sources, the COP-side
  // fall-through flies to the fire centroid (a single-id open would let it rail too).
  | 'fire'
  | 'evacuationOrder'
  | 'evacuation'
  | 'incident'
  | 'community'
  // #426 assistance-request location — a pure point kind: the COP-side handler's
  // fall-through ("any future point kind") flies to {lat,lon}, no entity to resolve.
  | 'assistance';

export interface BridgeSelection {
  kind: BridgeSelectionKind;
  id: string;
  /** Centroid for the COP fly-to (optional — camera/cap resolve their own). */
  lat?: number;
  lon?: number;
}

interface SelectionMessage {
  type: 'select';
  selection: BridgeSelection;
}

function isSelectionMessage(data: unknown): data is SelectionMessage {
  if (typeof data !== 'object' || data === null) return false;
  const m = data as Record<string, unknown>;
  if (m.type !== 'select' || typeof m.selection !== 'object' || m.selection === null) return false;
  const s = m.selection as Record<string, unknown>;
  return typeof s.kind === 'string' && typeof s.id === 'string';
}

/** Panel-side: hand a selection to the COP. No-op without BroadcastChannel. */
export function broadcastSelection(selection: BridgeSelection): void {
  if (typeof BroadcastChannel === 'undefined') return;
  const channel = new BroadcastChannel(SELECTION_CHANNEL_NAME);
  channel.postMessage({ type: 'select', selection } satisfies SelectionMessage);
  channel.close();
}

/** Same-window DOM event mirroring the cross-window broadcast. A BroadcastChannel
 *  does NOT deliver a message to the window that posted it, so an in-window caller
 *  (e.g. the #185 cross-link in a COP-window modal) needs this DOM path to reach
 *  the same window's `useSelectionBridge`. */
export const COP_SELECT_EVENT = 'tsr:cop-select';

/**
 * In-window: drive the COP's one-selection model from another component in the
 * SAME window — the cross-link counterpart to `broadcastSelection` (which only
 * reaches OTHER windows). Used by the alert↔ASSIST cross-link (#185) so an assist
 * detail modal can select+fly the COP to its connected Urgent Alert.
 */
export function selectOnCop(selection: BridgeSelection): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<BridgeSelection>(COP_SELECT_EVENT, { detail: selection }));
}

/** COP-side: run `onSelect` whenever a selection arrives — from another window
 *  (BroadcastChannel) OR from the same window (`selectOnCop`). */
export function useSelectionBridge(onSelect: (selection: BridgeSelection) => void): void {
  useEffect(() => {
    const channel =
      typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(SELECTION_CHANNEL_NAME);
    if (channel) {
      channel.onmessage = (e: MessageEvent) => {
        if (isSelectionMessage(e.data)) onSelect(e.data.selection);
      };
    }
    const onCopSelect = (e: Event): void => {
      const detail = (e as CustomEvent<BridgeSelection>).detail;
      if (detail && typeof detail.kind === 'string' && typeof detail.id === 'string') {
        onSelect(detail);
      }
    };
    if (typeof window !== 'undefined') window.addEventListener(COP_SELECT_EVENT, onCopSelect);
    return () => {
      channel?.close();
      if (typeof window !== 'undefined') window.removeEventListener(COP_SELECT_EVENT, onCopSelect);
    };
  }, [onSelect]);
}

/* ──────────────────────────────────────────────────────────────────────────
 * ESCALATE-TO-WALL (#222)
 *
 * The transient selection bridge above *flies* the COP/wall to a point and
 * forgets it. Escalate-to-wall is the deliberate, STICKY act of an operator
 * promoting one incident/camera to the shared room display: the wall holds it
 * (hero frames it, the media zone surfaces its nearest feeds + PAUSES its reel,
 * an incident's profile surfaces in the right zone) until the operator clears it
 * or it expires.
 *
 * Because the wall is a SEPARATE same-origin window — and may be opened AFTER an
 * operator escalates — the active target has to be both broadcast (live push to
 * open walls) and persisted (so a freshly-opened wall picks up the current pin).
 * We mirror `pinnedFeeds.ts` exactly: a localStorage cache (durable first-paint
 * source of truth) + a same-origin BroadcastChannel + a `storage`-event fallback.
 *
 * Migration-free by design (like pinnedFeeds): this is ephemeral cross-window UI
 * orchestration with no server state. (If founder later wants provenance — who
 * put what on the GOC wall, when — that's an audited `escalate_to_wall` verb +
 * migration, deliberately deferred.)
 * ────────────────────────────────────────────────────────────────────────── */

export const WALL_ESCALATION_KEY = 'tsr.wallEscalation';
export const WALL_ESCALATION_CHANNEL = 'tsr-wall-escalation';

/** How long an escalation stays on an unattended wall before it auto-clears, so a
 *  stale pin can never linger forever (calm-room rule — the wall returns to its
 *  alert-driven default). */
export const WALL_ESCALATION_TTL_MS = 30 * 60_000;

/** Camera → frame + force that feed; incident → frame + surface profile + nearest
 *  feeds. All other COP entities map to `incident` (incident-like point target). */
export type WallTargetKind = 'camera' | 'incident';

/** A read-only profile row shown in the wall's incident-profile zone. */
export interface WallTargetDetail {
  label: string;
  value: string;
}

export interface WallTarget {
  kind: WallTargetKind;
  /** Stable id (camera uuid / source:id) — the wall keys + re-fetches by it. */
  id: string;
  /** #428 — the resolved tracked incidents.id for the escalated incident (sectors
   *  FK to it). Packed by the COP escalate control when the source has been resolved
   *  to a tracked incident; the wall drives its incident-scoped sector overlay from
   *  it. DISTINCT from `id` (the raw source-record id the wall keys the pin by).
   *  Absent for cameras / unresolved sources → the wall renders no sectors. */
  incidentId?: string;
  /** Primary line (camera name / incident title). */
  label: string;
  /** Centroid for the hero fly-to + nearest-feed surfacing. */
  lat?: number;
  lon?: number;
  /** Secondary line (e.g. hazard + status) for the profile / caption. */
  sublabel?: string;
  /** Payload-carried read-only profile rows (wall stays a pure receiver — it does
   *  not re-fetch the incident; the COP packs what it already has loaded). */
  details?: WallTargetDetail[];
  /** Operator clock stamp (ms since epoch) the wall uses to expire a stale pin. */
  escalatedAtMs?: number;
}

interface EscalationMessage {
  type: 'wall:escalate';
  /** null = clear (un-escalate). */
  target: WallTarget | null;
}

function isWallTargetDetail(v: unknown): v is WallTargetDetail {
  if (typeof v !== 'object' || v === null) return false;
  const d = v as Record<string, unknown>;
  return typeof d.label === 'string' && typeof d.value === 'string';
}

/** Defensive parse of a stored/broadcast target (drops malformed fields). */
export function parseWallTarget(raw: unknown): WallTarget | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const t = raw as Record<string, unknown>;
  if ((t.kind !== 'camera' && t.kind !== 'incident') || typeof t.id !== 'string') return null;
  if (typeof t.label !== 'string') return null;
  const out: WallTarget = { kind: t.kind, id: t.id, label: t.label };
  if (typeof t.incidentId === 'string') out.incidentId = t.incidentId;
  if (typeof t.lat === 'number' && Number.isFinite(t.lat)) out.lat = t.lat;
  if (typeof t.lon === 'number' && Number.isFinite(t.lon)) out.lon = t.lon;
  if (typeof t.sublabel === 'string') out.sublabel = t.sublabel;
  if (Array.isArray(t.details)) out.details = t.details.filter(isWallTargetDetail);
  if (typeof t.escalatedAtMs === 'number' && Number.isFinite(t.escalatedAtMs)) {
    out.escalatedAtMs = t.escalatedAtMs;
  }
  return out;
}

/** Pure staleness check — a target older than the TTL is treated as cleared. Kept
 *  free of `Date.now()` so the wall (and tests) supply the clock. */
export function isEscalationStale(target: WallTarget | null, nowMs: number): boolean {
  if (!target) return false;
  if (target.escalatedAtMs == null) return false;
  return nowMs - target.escalatedAtMs > WALL_ESCALATION_TTL_MS;
}

export function loadWallEscalation(): WallTarget | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(WALL_ESCALATION_KEY);
    return raw ? parseWallTarget(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

function saveWallEscalation(target: WallTarget | null): void {
  if (typeof localStorage === 'undefined') return;
  try {
    if (target) localStorage.setItem(WALL_ESCALATION_KEY, JSON.stringify(target));
    else localStorage.removeItem(WALL_ESCALATION_KEY);
  } catch {
    /* quota / private mode — the broadcast still syncs live windows this session. */
  }
}

function broadcastEscalation(target: WallTarget | null): void {
  if (typeof BroadcastChannel === 'undefined') return;
  const channel = new BroadcastChannel(WALL_ESCALATION_CHANNEL);
  channel.postMessage({ type: 'wall:escalate', target } satisfies EscalationMessage);
  channel.close();
}

/** COP-side: promote a target to the wall (persist + broadcast). Stamps the
 *  operator clock so the wall can expire it. */
export function escalateToWall(target: WallTarget): void {
  const stamped: WallTarget = {
    ...target,
    escalatedAtMs: target.escalatedAtMs ?? (typeof Date !== 'undefined' ? Date.now() : undefined),
  };
  saveWallEscalation(stamped);
  broadcastEscalation(stamped);
}

/** COP-side: clear the active escalation (return the wall to its default). */
export function clearWallEscalation(): void {
  saveWallEscalation(null);
  broadcastEscalation(null);
}

function isEscalationMessage(data: unknown): data is EscalationMessage {
  if (typeof data !== 'object' || data === null) return false;
  const m = data as Record<string, unknown>;
  return m.type === 'wall:escalate' && (m.target === null || typeof m.target === 'object');
}

/**
 * Cross-window escalation store. Used by BOTH sides:
 *   • the wall (`WallView`) reads the active target to frame the hero, surface
 *     feeds + pause the reel, and show the incident profile;
 *   • the COP rail reads it to toggle its "Send to wall / On wall" control.
 * Live updates arrive via the channel; the `storage` event is the fallback for a
 * window that missed a broadcast; first paint reads the durable localStorage copy.
 */
export function useWallEscalation(): WallTarget | null {
  const [target, setTarget] = useState<WallTarget | null>(() => loadWallEscalation());

  useEffect(() => {
    const channel =
      typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(WALL_ESCALATION_CHANNEL);
    if (channel) {
      channel.onmessage = (e: MessageEvent) => {
        if (isEscalationMessage(e.data)) setTarget(e.data.target ? parseWallTarget(e.data.target) : null);
      };
    }
    const onStorage = (e: StorageEvent): void => {
      if (e.key === WALL_ESCALATION_KEY) setTarget(loadWallEscalation());
    };
    if (typeof window !== 'undefined') window.addEventListener('storage', onStorage);
    return () => {
      channel?.close();
      if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
    };
  }, []);

  return target;
}
